import React from "react";
import { useState, useEffect } from "react";
import { useToast } from "../../components/ui/use-toast";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Download, Upload, Eye, Trash, User, Users, ShoppingCart, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  questionsAPI,
  categoriesAPI,
  questionTypesAPI,
  questionBankAPI,
} from "../../services/api";

// Import the components
import UploadQuestionBanksModal from "./question-banks/UploadQuestionBanksModal";
import CreateQuestionBankModal from "./question-banks/CreateQuestionBankModal";
import SampleDownloadModal from "./question-banks/SampleDownloadModal";
import ModernPageTemplate, { 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";
import ViewQuestionsModal from "../../components/admin/ViewQuestionsModal";
import { format } from "date-fns";

export default function QuestionDetails() {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Constants
  const QUESTION_BANKS_PER_PAGE = 10;

  // State management
  const [userCategories, setUserCategories] = useState([]);
  const [companyCategories, setCompanyCategories] = useState([]);
  const [questionBanks, setQuestionBanks] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteBankName, setDeleteBankName] = useState("");
  const [samplePopupOpen, setSamplePopupOpen] = useState(false);
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // View questions modal state
  const [viewQuestionsModalOpen, setViewQuestionsModalOpen] = useState(false);
  const [selectedBankForView, setSelectedBankForView] = useState(null);
  
  // Modern state management
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("user");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(QUESTION_BANKS_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  const [totalBanks, setTotalBanks] = useState(0);
  
  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      if (window.innerWidth >= 1024) {
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            footer.style.left = '256px';
          }
        }
      } else {
        footer.style.left = '0px';
      }
    };

    adjustFooterPosition();
    window.addEventListener('resize', adjustFooterPosition);

    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  // Utility functions
  const showToast = (type, title, description) => {
    toast({
      title,
      description,
      variant: type === "error" ? "destructive" : undefined,
    });
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortBanks = (banks, sortField, sortOrder) => {
    // If no sorting is applied, return banks in their original order (newest first from API)
    if (!sortField) return banks;

    return [...banks].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdAt') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Fetch question banks based on active tab
  const fetchQuestionBank = async () => {
    try {
      setIsLoading(true);
      const params = {
        status: "draft",
        page: isPaginationEnabled ? currentPage : 1,
        limit: isPaginationEnabled ? rowsPerPage : 999999,
        search: searchTerm,
        filter: activeTab, // 'user', 'company', or 'purchased'
      };

      const response = await questionBankAPI.getAll(params);
      
      // Set question banks and total based on active tab
      if (activeTab === "user") {
        setQuestionBanks(response.data.banks || []);
        setTotalBanks(response.data.pagination?.totalItems || 0);
      } else if (activeTab === "company") {
        setQuestionBanks(response.data.companyQuestionBanks || []);
        setTotalBanks(response.data.pagination?.totalItems || 0);
      } else if (activeTab === "purchased") {
        setQuestionBanks(response.data.purchasedQuestionBanks || []);
        setTotalBanks(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error("Failed to fetch question banks:", error);
      showToast("error", "Error", "Failed to load question banks");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoriesForDownload = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setUserCategories(response.data.userCategories || []);
      setCompanyCategories(response.data.companyCategories || []);
    } catch (error) {
      console.error("Error fetching categories for download:", error);
      toast({
        title: "Error",
        description: "Failed to fetch categories",
        variant: "destructive",
      });
    }
  };

  const fetchFilteredCategories = async (questionBankName) => {
    try {
      const response = await categoriesAPI.getFiltered(questionBankName);
      setUserCategories(response.data.userCategories || []);
      setCompanyCategories(response.data.companyCategories || []);
    } catch (error) {
      console.error("Error fetching filtered categories:", error);
      toast({
        title: "Error",
        description: "Failed to fetch filtered categories",
        variant: "destructive",
      });
    }
  };

  const fetchQuestionTypes = async () => {
    try {
      const response = await questionTypesAPI.getAll();
      setQuestionTypes(response.data.questionTypes || []);
    } catch (error) {
      console.error("Failed to fetch question types:", error);
      showToast("error", "Error", "Failed to load question types");
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await questionBankAPI.delete(deleteId);
      if (response.status === 200) {
        fetchQuestionBank();
        setShowDeleteDialog(false);
        showToast(
          "success",
          "Success",
          "Question bank and all related questions deleted successfully"
        );
      }
    } catch (error) {
      console.error("Failed to delete question bank:", error);
      showToast(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to delete question bank"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  // Modern handlers
  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveTab('user');
    setSortField('');
    setSortOrder('asc');
    setShowCreatePopup(false);
    await fetchQuestionBank();
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePaginationToggle = (enabled) => {
    setIsPaginationEnabled(enabled);
    if (!enabled) {
      setCurrentPage(1);
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleViewQuestionBank = (bank) => {
    setSelectedBankForView(bank);
    setViewQuestionsModalOpen(true);
    
    // Update URL without navigation
    const url = new URL(window.location);
    url.searchParams.set('viewBank', bank._id);
    window.history.pushState({}, '', url);
  };

  const handleCloseViewModal = (open) => {
    setViewQuestionsModalOpen(open);
    if (!open) {
      setSelectedBankForView(null);
      // Clean up URL
      const url = new URL(window.location);
      url.searchParams.delete('viewBank');
      window.history.pushState({}, '', url);
    }
  };

  const handleDeleteQuestionBank = (bank) => {
    setDeleteId(bank._id);
    setDeleteBankName(bank.name);
    setShowDeleteDialog(true);
  };

  // Effect hooks
  useEffect(() => {
    fetchQuestionBank();
    fetchQuestionTypes();
  }, [searchTerm, activeTab, currentPage, rowsPerPage, isPaginationEnabled]);

  // Reset page when search/tab changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search/tab changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm, activeTab]);

  const handleDownloadSample = () => {
    fetchCategoriesForDownload();
    setSamplePopupOpen(true);
  };

  const handleCreateQuestionBank = () => {
    fetchFilteredCategories();
    setShowCreatePopup(true);
  };

  const handleUploadSuccess = () => {
    fetchQuestionBank();
  };

  // Render question banks table
  const renderQuestionBanksTable = () => {
    const banks = questionBanks || [];
    
    // Sort the banks
    const sortedBanks = sortBanks(banks, sortField, sortOrder);

    return (
      <>
        {isLoading ? (
          <ModernLoading text="Loading question banks..." />
        ) : sortedBanks.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead className="admin-table-header">
                <tr>
                  <th className="admin-table-cell">S.No</th>
                  <th
                    className="admin-table-cell sortable"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center cursor-pointer">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="admin-table-cell sortable"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center cursor-pointer">
                      Description
                      {getSortIcon('description')}
                    </div>
                  </th>
                  <th
                    className="admin-table-cell sortable"
                    onClick={() => handleSort('questionCount')}
                  >
                    <div className="flex items-center cursor-pointer">
                      Total Questions
                      {getSortIcon('questionCount')}
                    </div>
                  </th>
                  <th className="admin-table-cell">Capacity</th>
                  <th className="admin-table-cell">Usage</th>
                  <th
                    className="admin-table-cell sortable"
                    onClick={() => handleSort('createdBy.name')}
                  >
                    <div className="flex items-center cursor-pointer">
                      Created By
                      {getSortIcon('createdBy.name')}
                    </div>
                  </th>
                  <th
                    className="admin-table-cell sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center cursor-pointer">
                      Created At
                      {getSortIcon('createdAt')}
                    </div>
                  </th>
                  <th className="admin-table-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBanks.map((bank, index) => {
                  const currentCount = bank.questionCount || 0;
                  const maxCapacity = bank.maxCapacity || 5000;
                  const percentage = Math.round((currentCount / maxCapacity) * 100);
                  
                  return (
                    <tr key={bank._id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {isPaginationEnabled ? (currentPage - 1) * rowsPerPage + index + 1 : index + 1}
                      </td>
                      <td className="admin-table-cell font-medium">
                        <span className="text-sm">{bank.name || "Unnamed Bank"}</span>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm text-gray-600">
                          {bank.description || "No description"}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <ModernBadge variant="language">
                          <span className="text-xs">{currentCount}</span>
                        </ModernBadge>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm">{currentCount}/{maxCapacity}</span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${percentage >= 90 ? 'bg-red-500' : percentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{percentage}%</span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        {bank.createdBy ? (
                          <ModernBadge variant="template">
                            <span className="text-xs">{bank.createdBy.name || bank.createdBy.email || "Unknown"}</span>
                          </ModernBadge>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-xs">
                          {bank.createdAt ? format(new Date(bank.createdAt), "MMM dd, yyyy") : "N/A"}
                        </span>
                      </td>
                      <td className="admin-table-cell text-right">
                        <div className="admin-table-actions">
                          <button
                            className="admin-table-action-btn view"
                            onClick={() => handleViewQuestionBank(bank)}
                            title="View questions"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          {activeTab !== "purchased" && (
                            <button
                              className="admin-table-action-btn delete"
                              onClick={() => handleDeleteQuestionBank(bank)}
                              title="Delete question bank"
                            >
                              <Trash className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No question banks found
          </div>
        )}
      </>
    );
  };

  // Main render
  return (
    <ModernPageTemplate
      title="Question Banks"
      contentClassName="question-banks-page"
      stats={{
        total: totalBanks,
        customStats: (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="categories-header-tabs-list">
              <TabsTrigger value="user" className="categories-header-tab">
                <User className="h-3 w-3" />
                User Banks
              </TabsTrigger>
              <TabsTrigger value="company" className="categories-header-tab">
                <Users className="h-3 w-3" />
                Company Banks
              </TabsTrigger>
              <TabsTrigger value="purchased" className="categories-header-tab">
                <ShoppingCart className="h-3 w-3" />
                Purchased Banks
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search question banks...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      headerAction={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadSample}
            title="Download Sample"
          >
            <Download className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUploadPopupOpen(true)}
            title="Upload Question Banks"
          >
            <Upload className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCreateQuestionBank}
            title="Create Question Banks"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      {renderQuestionBanksTable()}

      {/* Modern Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: Math.ceil(totalBanks / rowsPerPage) || 1,
          total: totalBanks
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      {/* Modals */}
      <CreateQuestionBankModal
        open={showCreatePopup}
        onOpenChange={setShowCreatePopup}
        userCategories={userCategories}
        companyCategories={companyCategories}
        questionTypes={questionTypes}
        fetchFilteredCategories={fetchFilteredCategories}
        onSuccess={fetchQuestionBank}
        showToast={showToast}
      />

      <SampleDownloadModal
        open={samplePopupOpen}
        onOpenChange={setSamplePopupOpen}
        userCategories={userCategories}
        companyCategories={companyCategories}
      />

      <UploadQuestionBanksModal
        open={uploadPopupOpen}
        onOpenChange={setUploadPopupOpen}
        dropDownQuestionBank={questionBanks}
        onUploadSuccess={handleUploadSuccess}
        showToast={showToast}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question Bank?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the question bank "{deleteBankName}"?
              This will also delete all questions in this bank. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteId("");
                setDeleteBankName("");
              }}
              disabled={deleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Questions Modal */}
      <ViewQuestionsModal
        open={viewQuestionsModalOpen}
        onOpenChange={handleCloseViewModal}
        questionBank={selectedBankForView}
      />
    </ModernPageTemplate>
  );
}
